import { useState } from 'react';

const NOT_HEX = /[^0-9a-f]/gi;

interface HexInputProps {
  id?: string;
  className?: string;
  /** the current color as 6 hex digits, without '#' */
  value: string;
  /** called with a full '#rrggbb' color */
  onChange: (hex: string) => void;
}

/**
 * The hex field of the color picker. What the user is typing is theirs until they leave the field: it's
 * never rewritten from props, so a color coming back from the parent can't fill in or overwrite a
 * half-typed value. A full 6 digits applies as it's typed; a 3-digit shorthand applies when the field
 * is left (blur or Enter), and anything else then reverts to the current color.
 */
export default function HexInput({ id, className, value, onChange }: HexInputProps) {
  // what the user has typed since they started editing - null shows the current color instead
  const [draft, setDraft] = useState<string | null>(null);

  const finishEditing = () => {
    if (draft?.length === 3) {
      onChange(`#${[...draft].map((digit) => digit + digit).join('')}`);
    }
    setDraft(null);
  };

  return (
    <input
      id={id}
      aria-label="Hex color"
      className={className}
      value={draft ?? value}
      spellCheck={false}
      autoComplete="off"
      onChange={(e) => {
        const next = e.target.value.replace(NOT_HEX, '').slice(0, 6);
        setDraft(next);
        if (next.length === 6) onChange(`#${next}`);
      }}
      onBlur={finishEditing}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finishEditing();
      }}
    />
  );
}
